import { type ComponentType, type ReactElement, type SVGProps } from 'react';
import { Images } from '@/assets/images';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { HighlightBox } from '@/components/shared/HighlightBox';
import { getPlatformIcon } from '@/components/shared/platform/platform-utils';
import { cn } from '@/lib/utils';
import { ArrowRight, TriangleAlert } from 'lucide-react';

/** The three install paths surfaced as selectable cards. */
export type SetupCardId = 'agent' | 'cursor' | 'vscode';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

interface SetupCardMeta {
  id: SetupCardId;
  title: string;
  description: string;
  /** Tool logos shown in the card's icon row. */
  icons: { Icon: IconComponent; label: string }[];
}

const CARD_META: Record<SetupCardId, SetupCardMeta> = {
  agent: {
    id: 'agent',
    title: 'Kirin Agent',
    description: 'One install for all supported AI tools.',
    // Agent icon row is data-driven from the released platforms (see
    // `agentToolIds`); the static list is only used for the extension cards.
    icons: [],
  },
  cursor: {
    id: 'cursor',
    title: 'Kirin for Cursor',
    description:
      'Cursor extension. Requires the Kirin Agent for enforcement actions.',
    icons: [{ Icon: Images.CursorIcon, label: 'Cursor' }],
  },
  vscode: {
    id: 'vscode',
    title: 'Kirin for VSCode Copilot',
    description:
      'VSCode Copilot extension. Requires the Kirin Agent for enforcement actions.',
    icons: [{ Icon: Images.VSCodeIcon, label: 'VSCode' }],
  },
};

interface SetupCardProps {
  meta: SetupCardMeta;
  selected: boolean;
  onSelect: (id: SetupCardId) => void;
  /** For the Agent card: released platform ids whose icons fill the icon row. */
  agentToolIds: string[];
}

function SetupCard({
  meta,
  selected,
  onSelect,
  agentToolIds,
}: SetupCardProps): ReactElement {
  const icons =
    meta.id === 'agent'
      ? agentToolIds.map(id => ({
          key: id,
          Icon: getPlatformIcon(id),
        }))
      : meta.icons.map(({ Icon, label }) => ({ key: label, Icon }));
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={meta.title}
      onClick={() => onSelect(meta.id)}
      className={cn(
        // Constant border width + ring keep the box size identical across
        // states, so selecting a card never changes its width (ticket note #1).
        'flex flex-col items-start gap-2 rounded-lg border p-4 text-left transition-colors',
        'hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected
          ? 'border-primary bg-blue-50 dark:bg-blue-950/30'
          : 'border-border bg-card'
      )}
    >
      <div className="flex flex-wrap items-center gap-2" aria-hidden="true">
        {icons.map(({ key, Icon }) => (
          <span
            key={key}
            className="inline-flex shrink-0 items-center justify-center rounded-md border bg-background p-1"
          >
            <Icon className="size-5" />
          </span>
        ))}
      </div>
      <div className="font-semibold">{meta.title}</div>
      <div className="text-sm text-muted-foreground">{meta.description}</div>
    </button>
  );
}

interface InstallCardSelectorProps {
  selected: SetupCardId;
  onSelect: (id: SetupCardId) => void;
  /** When false the VSCode Copilot card is hidden (deprecation flag). */
  showVSCode: boolean;
  /** Released agent platform ids whose icons appear in the Agent card row. */
  agentToolIds: string[];
}

/**
 * Three equal-width selectable cards for the Step 2 & 3 install flow. The
 * grid column count adapts when the VSCode card is hidden so the remaining
 * cards keep equal width.
 */
export function InstallCardSelector({
  selected,
  onSelect,
  showVSCode,
  agentToolIds,
}: InstallCardSelectorProps): ReactElement {
  const cards: SetupCardId[] = showVSCode
    ? ['agent', 'cursor', 'vscode']
    : ['agent', 'cursor'];
  return (
    <div
      role="group"
      aria-label="Choose your Kirin install"
      className={cn(
        'grid gap-4',
        showVSCode ? 'sm:grid-cols-3' : 'sm:grid-cols-2'
      )}
    >
      {cards.map(id => (
        <SetupCard
          key={id}
          meta={CARD_META[id]}
          selected={selected === id}
          onSelect={onSelect}
          agentToolIds={agentToolIds}
        />
      ))}
    </div>
  );
}

interface SetupFrameProps {
  cardId: SetupCardId;
  children: React.ReactNode;
}

/** Bordered "SETUP FOR <name>" frame that holds the selected card's flow. */
export function SetupFrame({
  cardId,
  children,
}: SetupFrameProps): ReactElement {
  return (
    <div className="rounded-lg border bg-muted/50 p-6 mt-4">
      <div className="mb-6">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Setup for
        </div>
        <h2 className="text-lg font-semibold">{CARD_META[cardId].title}</h2>
        <div className="mt-3 border-b" />
      </div>
      <div className="space-y-6">{children}</div>
    </div>
  );
}

interface AgentPrerequisiteBannerProps {
  onInstallAgent: () => void;
}

/**
 * Yellow prerequisite banner shown on the Cursor / VSCode frames. The
 * "Install Kirin Agent →" action switches the selector back to the Agent card.
 */
export function AgentPrerequisiteBanner({
  onInstallAgent,
}: AgentPrerequisiteBannerProps): ReactElement {
  return (
    <HighlightBox variant="warning" padding="lg" role="note">
      <div className="flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-2">
          <TriangleAlert className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div>
            <strong>
              Required if not already installed: Install the Kirin Agent.
            </strong>{' '}
            Without the Kirin Agent, this extension provides monitoring and
            visibility only. Policy enforcement will not be active.
          </div>
        </div>
        <Button
          variant="link"
          className="h-auto shrink-0 self-start p-0 text-amber-700 dark:text-amber-300"
          onClick={onInstallAgent}
        >
          Install Kirin Agent
          <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </HighlightBox>
  );
}

interface AgentCoverageBannerProps {
  /** Display labels of released agent tools, rendered as highlighted badges. */
  highlighted: string[];
  /** Display labels rendered as COMING SOON outline badges. */
  comingSoon: string[];
}

/**
 * Coverage banner inside the Agent frame: "One install for all supported AI
 * tools" with highlighted badges for released agent platforms and COMING SOON
 * outline badges for the rest.
 */
export function AgentCoverageBanner({
  highlighted,
  comingSoon,
}: AgentCoverageBannerProps): ReactElement {
  return (
    <HighlightBox variant="info" padding="lg">
      <div className="space-y-3 text-sm">
        <div className="font-medium">
          One install for all supported AI tools:
        </div>
        <div className="flex flex-wrap gap-2">
          {highlighted.map(label => (
            <Badge key={label} variant="default">
              {label}
            </Badge>
          ))}
          {comingSoon.map(label => (
            <Badge
              key={label}
              variant="outline"
              className="text-muted-foreground"
            >
              {label} · COMING SOON
            </Badge>
          ))}
        </div>
      </div>
    </HighlightBox>
  );
}
